// @lymon/widget-sdk-host — plugin-widget host (postMessage, permissions, subs).
// MIRROR of services/web/src/designer/widget-sdk/host.ts — kept in sync by hand
// so the design editor (services/web) can host plugin bundles without a
// cross-service import. If you change one, change both. Vue-agnostic (plain DOM).
//
// Public API:
//   const host = createPluginWidgetHost({
//     iframe,
//     bundle,           // { html, manifest }
//     instanceConfig,   // operator's per-instance config + permission overrides
//     dataBridge,       // adapter to lymon's data plane (POST /api/embed/data)
//     contextProvider,
//     tenantId,
//     widgetInstanceId,
//   });
//   host.start();
//   host.destroy();
//
// The host owns:
//   - the postMessage listener (filtered to this iframe's contentWindow)
//   - the permission gate (computed from bundle manifest + instance config)
//   - data subscription bookkeeping (subId → polling timer over fetchData)
//   - storage namespace ('lymon.widget.<tenant>.<widget>.<key>')
//   - context broadcasts on dashboard variable / time-range changes

import {
  PROTOCOL_VERSION,
  DEFAULT_PERMISSIONS,
  isWidgetMessage,
  type HostMessage,
  type Permissions,
  type WidgetContext,
  type DataQuery,
  type DataResult,
} from './protocol';

import type { WidgetManifest } from './manifest';

// ---------------------------------------------------------------------
// Bridge — the host needs an adapter to talk to lymon's data plane.
// The concrete implementation (SynEmbed.vue) POSTs to /api/embed/data
// with the embed token. Keeping the SDK pure makes it unit-testable
// without spinning up the whole platform.
// ---------------------------------------------------------------------

export interface DataBridge {
  /** Resolve a binding once → typed result (scalar/series/table). */
  fetchData(query: DataQuery): Promise<DataResult>;

  /** ADR 50 — subscribe a binding to live SCALAR value pushes (server-pushed on
   *  change, no polling). Optional: when absent, the host falls back to polling
   *  fetchData. Returns an unsubscribe fn. Used only for scalar queries
   *  (wantSeries omitted); series subscriptions keep polling until the live hub
   *  streams series too. */
  subscribeData?(query: DataQuery, onValue: (result: DataResult) => void): () => void;

  /** Invoke a registered host command (e.g. navigate, export). */
  invokeCommand(id: string, args: unknown): Promise<unknown>;

  /** Set a dashboard variable. */
  setVariable(name: string, value: unknown): Promise<void>;

  /** Evaluate a Lymon expression. Gated by the `eval` permission (default false). */
  evalExpression(expression: string, refs: Record<string, unknown>): Promise<number | string | boolean | null>;
}

// ---------------------------------------------------------------------
// Per-instance config — set by the operator when adding the widget
// to a dashboard. Includes the values for each `inputs` field declared
// in the manifest, plus optional permission narrowing.
// ---------------------------------------------------------------------

export interface InstanceConfig {
  /** Operator-filled values for the manifest's `inputs`. */
  config: Record<string, unknown>;
  /** Optional permission overrides (can NARROW, never escalate). */
  permissions?: Partial<Permissions>;
}

// ---------------------------------------------------------------------
// Context provider — Lymon dashboard publishes vars / timeRange / theme
// changes; the host re-broadcasts to widget.
// ---------------------------------------------------------------------

export interface ContextProvider {
  current(): Omit<WidgetContext, 'protocol'>;
  /** Returns an unsubscribe fn. */
  onChange(handler: () => void): () => void;
}

// ---------------------------------------------------------------------
// Storage namespace
// ---------------------------------------------------------------------

export interface StorageAdapter {
  read(): Record<string, unknown>;
  write(key: string, value: unknown): void;
  remove(key: string): void;
}

export function createLocalStorageAdapter(prefix: string): StorageAdapter {
  const ensurePrefix = (k: string): string => `${prefix}${k}`;
  return {
    read(): Record<string, unknown> {
      const out: Record<string, unknown> = {};
      if (typeof localStorage === 'undefined') return out;
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (!k || !k.startsWith(prefix)) continue;
          const raw = localStorage.getItem(k);
          if (raw === null) continue;
          try {
            out[k.slice(prefix.length)] = JSON.parse(raw);
          } catch {
            out[k.slice(prefix.length)] = raw;
          }
        }
      } catch {
        /* localStorage unavailable */
      }
      return out;
    },
    write(key: string, value: unknown): void {
      if (typeof localStorage === 'undefined') return;
      try {
        localStorage.setItem(ensurePrefix(key), JSON.stringify(value));
      } catch {
        /* quota exceeded etc. */
      }
    },
    remove(key: string): void {
      if (typeof localStorage === 'undefined') return;
      try {
        localStorage.removeItem(ensurePrefix(key));
      } catch {
        /* ignore */
      }
    },
  };
}

// ---------------------------------------------------------------------
// Permission resolution
// ---------------------------------------------------------------------

export function resolvePermissions(
  manifest: Pick<WidgetManifest, 'permissions'>,
  override: Partial<Permissions> | undefined,
): Permissions {
  const fromManifest = manifest.permissions ?? {};

  // commands has a special "least-of" rule that doesn't fit a generic
  // ??-chain because we need to intersect arrays.
  const commands = narrowCommands(
    DEFAULT_PERMISSIONS.commands,
    fromManifest.commands,
    override?.commands,
  );

  return {
    data: override?.data ?? fromManifest.data ?? DEFAULT_PERMISSIONS.data,
    storage: override?.storage ?? fromManifest.storage ?? DEFAULT_PERMISSIONS.storage,
    commands,
    setVariable:
      override?.setVariable ?? fromManifest.setVariable ?? DEFAULT_PERMISSIONS.setVariable,
    eval: override?.eval ?? fromManifest.eval ?? DEFAULT_PERMISSIONS.eval,
  };
}

function narrowCommands(
  ...layers: (Permissions['commands'] | undefined)[]
): Permissions['commands'] {
  // Walk from most-permissive (defaults) to most-restrictive (override).
  // Each layer can shrink the set but never grow it.
  let current: Permissions['commands'] = DEFAULT_PERMISSIONS.commands;
  for (const layer of layers) {
    if (layer === undefined) continue;
    if (layer === false) return false;             // hard lockdown wins
    if (current === false) return false;
    if (layer === true) {
      // 'true' as a layer means "no narrowing" — keep current
      continue;
    }
    // layer is string[] — intersect with current
    if (current === true) {
      current = [...layer];
    } else {
      current = current.filter((id) => layer.includes(id));
    }
  }
  return current;
}

// ---------------------------------------------------------------------
// The host itself
// ---------------------------------------------------------------------

export interface CreateHostOpts {
  iframe: HTMLIFrameElement;
  bundle: { html: string; manifest: WidgetManifest };
  instanceConfig: InstanceConfig;
  dataBridge: DataBridge;
  contextProvider: ContextProvider;
  tenantId: string;
  /** The widget instance id — used to namespace storage. */
  widgetInstanceId: string;
  /** Optional log sink. Default: parent's console. */
  log?: (level: string, args: unknown[]) => void;
}

export interface PluginWidgetHost {
  start(): void;
  destroy(): void;
  permissions(): Permissions;
}

export function createPluginWidgetHost(opts: CreateHostOpts): PluginWidgetHost {
  const permissions = resolvePermissions(opts.bundle.manifest, opts.instanceConfig.permissions);
  const storage = createLocalStorageAdapter(
    `lymon.widget.${opts.tenantId}.${opts.widgetInstanceId}.`,
  );
  const log = opts.log ?? ((level, args) =>
    (console as unknown as Record<string, (...a: unknown[]) => void>)[level]?.(
      `[lymon:widget:${opts.widgetInstanceId}]`,
      ...args,
    ));

  type SubRecord = { dispose: (() => void) | null };
  const subs = new Map<string, SubRecord>();
  let contextUnsub: (() => void) | null = null;

  function post(msg: HostMessage): void {
    const win = opts.iframe.contentWindow;
    if (!win) return;
    win.postMessage(msg, '*');
  }

  function snapshotContext(): WidgetContext {
    return {
      protocol: PROTOCOL_VERSION,
      ...opts.contextProvider.current(),
    };
  }

  function sendReady(): void {
    post({
      kind: 'ready',
      protocol: PROTOCOL_VERSION,
      context: snapshotContext(),
      // Honor storage permission at boot — denied widgets get an empty bag.
      storage: permissions.storage ? storage.read() : {},
    });
  }

  // -------------------------------------------------------------------
  // Data plane — resolve a binding via the bridge. When the widget omits
  // a window, fall back to the dashboard's current time range so a plugin
  // tracks the design window exactly like a native element.
  // -------------------------------------------------------------------

  function withWindow(query: DataQuery): DataQuery {
    if (query.timeRange) return query;
    const ctx = opts.contextProvider.current();
    if (ctx.timeRange?.iso) return { ...query, timeRange: ctx.timeRange.iso };
    return query;
  }

  async function handleData(reqId: string, query: DataQuery): Promise<void> {
    if (!permissions.data) { post({ kind: 'data-error', reqId, message: 'permission denied: data' }); return; }
    try {
      const result = await opts.dataBridge.fetchData(withWindow(query));
      post({ kind: 'data-result', reqId, result });
    } catch (err) {
      post({ kind: 'data-error', reqId, message: err instanceof Error ? err.message : 'data failed' });
    }
  }

  function handleDataSubscribe(subId: string, query: DataQuery, intervalMs: number | undefined): void {
    if (!permissions.data) { post({ kind: 'data-sub-error', subId, message: 'permission denied: data' }); return; }
    if (subs.has(subId)) return;
    const rec: SubRecord = { dispose: null };
    subs.set(subId, rec);
    const q = withWindow(query);

    // ADR 50 — scalar live value → server push over WS (no poll). Series still
    // poll until the live hub streams series. The widget protocol is unchanged
    // (it still receives `data-value`).
    if (!q.wantSeries && opts.dataBridge.subscribeData) {
      let stopped = false;
      const unsub = opts.dataBridge.subscribeData(q, (result) => {
        if (!stopped) post({ kind: 'data-value', subId, result });
      });
      rec.dispose = () => { stopped = true; try { unsub(); } catch { /* ignore */ } };
      return;
    }

    const period = Math.max(1000, intervalMs ?? 5000);
    let stopped = false;
    const tick = async (): Promise<void> => {
      if (stopped) return;
      try {
        const result = await opts.dataBridge.fetchData(q);
        if (!stopped) post({ kind: 'data-value', subId, result });
      } catch (err) {
        if (!stopped) post({ kind: 'data-sub-error', subId, message: err instanceof Error ? err.message : 'data failed' });
      }
    };
    const timer = setInterval(() => void tick(), period);
    rec.dispose = () => { stopped = true; clearInterval(timer); };
    void tick(); // immediate first resolve
  }

  function handleUnsubscribe(subId: string): void {
    const rec = subs.get(subId);
    if (!rec) return;
    try { rec.dispose?.(); } catch { /* ignore */ }
    subs.delete(subId);
  }

  function commandAllowed(id: string): boolean {
    const c = permissions.commands;
    if (c === true) return true;
    if (c === false) return false;
    return Array.isArray(c) && c.includes(id);
  }

  async function handleCommand(reqId: string, id: string, args: unknown): Promise<void> {
    if (!commandAllowed(id)) {
      post({ kind: 'command-error', reqId, message: `permission denied for command "${id}"` });
      return;
    }
    try {
      const result = await opts.dataBridge.invokeCommand(id, args);
      // Strip non-cloneable bits (Vue refs, functions, DOM) before posting.
      let safe: unknown;
      try { safe = JSON.parse(JSON.stringify(result ?? null)); } catch { safe = null; }
      post({ kind: 'command-result', reqId, result: safe });
    } catch (err) {
      post({ kind: 'command-error', reqId, message: err instanceof Error ? err.message : 'command threw' });
    }
  }

  async function handleSetVariable(reqId: string, name: string, value: unknown): Promise<void> {
    if (!permissions.setVariable) {
      post({ kind: 'set-variable-error', reqId, message: 'permission denied: setVariable' });
      return;
    }
    try {
      await opts.dataBridge.setVariable(name, value);
      post({ kind: 'set-variable-result', reqId });
    } catch (err) {
      post({ kind: 'set-variable-error', reqId, message: err instanceof Error ? err.message : 'setVariable failed' });
    }
  }

  async function handleEval(reqId: string, expr: string, refs: Record<string, unknown>): Promise<void> {
    if (!permissions.eval) {
      post({ kind: 'eval-error', reqId, message: 'permission denied: eval' });
      return;
    }
    try {
      const value = await opts.dataBridge.evalExpression(expr, refs);
      post({ kind: 'eval-result', reqId, value });
    } catch (err) {
      post({ kind: 'eval-error', reqId, message: err instanceof Error ? err.message : 'eval failed' });
    }
  }

  function handleStorageSet(key: string, value: unknown): void {
    if (!permissions.storage) return;
    storage.write(key, value);
  }

  function handleStorageRemove(key: string): void {
    if (!permissions.storage) return;
    storage.remove(key);
  }

  // -------------------------------------------------------------------
  // Dispatcher
  // -------------------------------------------------------------------

  function onMessage(ev: MessageEvent): void {
    if (ev.source !== opts.iframe.contentWindow) return;
    if (!isWidgetMessage(ev.data)) return;
    const msg = ev.data;
    switch (msg.kind) {
      case 'ready':
        // Widget signals it imported the SDK. We already sent our `ready`
        // on iframe `load`; this is informational.
        break;
      case 'data':
        void handleData(msg.reqId, msg.query);
        break;
      case 'data-subscribe':
        handleDataSubscribe(msg.subId, msg.query, msg.intervalMs);
        break;
      case 'data-unsubscribe':
        handleUnsubscribe(msg.subId);
        break;
      case 'command':
        void handleCommand(msg.reqId, msg.id, msg.args);
        break;
      case 'set-variable':
        void handleSetVariable(msg.reqId, msg.name, msg.value);
        break;
      case 'eval':
        void handleEval(msg.reqId, msg.expression, msg.refs);
        break;
      case 'storage-set':
        handleStorageSet(msg.key, msg.value);
        break;
      case 'storage-remove':
        handleStorageRemove(msg.key);
        break;
      case 'log':
        log(msg.level, msg.args);
        break;
    }
  }

  function onFrameLoad(): void {
    // Drop any subs from a previous incarnation (operator re-edited
    // the widget HTML, triggering an iframe reload).
    for (const rec of subs.values()) {
      try { rec.dispose?.(); } catch { /* ignore */ }
    }
    subs.clear();
    sendReady();
  }

  function start(): void {
    opts.iframe.addEventListener('load', onFrameLoad);
    window.addEventListener('message', onMessage);
    contextUnsub = opts.contextProvider.onChange(() => {
      post({ kind: 'context', context: snapshotContext() });
    });
  }

  function destroy(): void {
    post({ kind: 'destroy' });
    for (const rec of subs.values()) {
      try { rec.dispose?.(); } catch { /* ignore */ }
    }
    subs.clear();
    opts.iframe.removeEventListener('load', onFrameLoad);
    window.removeEventListener('message', onMessage);
    contextUnsub?.();
  }

  return {
    start,
    destroy,
    permissions: () => permissions,
  };
}
